// Minimal accessible tabs (WAI-ARIA Tabs Pattern: role="tablist"/"tab"/
// "tabpanel", arrow-key roving focus). First use: `/manager/cases/:id`,
// which had grown to eight stacked cards with "Ask Maeve about this case"
// buried near the bottom.
//
// `TabPanel` hides inactive panels with the `hidden` attribute rather than
// conditionally rendering — several of this page's cards (RTW plan draft,
// Maeve guidance thread) hold state that must survive a tab switch, not
// just a caseId change. Keeping every panel mounted sidesteps having to
// reason about which of them is safe to unmount.

import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';

export interface TabDef {
  id: string;
  label: string;
  badge?: ReactNode;
}

// Backs a tab's active id with a `?<param>=` URL search param instead of
// local state — deep-linkable, survives refresh/back-forward, and (per
// `setSearchParams`, not component state) survives the route's own
// remounts. The default tab is deliberately omitted from the URL rather
// than written as `?tab=overview`, so the common case keeps a clean URL.
//
// An unrecognized value (a stale bookmark, a renamed tab id) falls back to
// the default for rendering AND is stripped from the URL on the next tick —
// otherwise the address bar would keep showing an invalid tab id
// indefinitely, which is misleading if that URL gets copied or bookmarked
// again.
export function useTabInSearchParam<T extends string>(
  paramName: string,
  tabIds: readonly T[],
  defaultTab: T,
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(paramName);
  const isValid = raw !== null && (tabIds as readonly string[]).includes(raw);
  const active = (isValid ? raw : defaultTab) as T;

  useEffect(() => {
    if (raw === null || isValid) return;
    const sp = new URLSearchParams(searchParams);
    sp.delete(paramName);
    setSearchParams(sp, { replace: true });
    // Deliberately narrow deps: only react to the raw param value changing,
    // not to searchParams/setSearchParams identity (both change on every
    // navigation, which would loop against this effect's own call).
  }, [raw, isValid]);

  function setActive(next: T) {
    const sp = new URLSearchParams(searchParams);
    if (next === defaultTab) sp.delete(paramName);
    else sp.set(paramName, next);
    setSearchParams(sp, { replace: true });
  }

  return [active, setActive];
}

// Moves DOM focus to a tab button. For a tab switched PROGRAMMATICALLY (a
// control inside one panel sending the user to another), where the WAI-ARIA
// tabs pattern still wants focus on the newly-selected tab — `TabList`'s own
// click handler already does the equivalent for a directly-clicked tab, and
// without this the trigger keeps focus inside a panel that is now `hidden`,
// which drops focus to `<body>`.
//
// Lives here so the `tab-${id}` element-id format stays owned by one file.
// Must be called AFTER the commit that reveals the panel (an effect, not the
// event handler) — `hidden` ancestors are not focusable.
export function focusTab(tabId: string): void {
  document.getElementById(`tab-${tabId}`)?.focus();
}

export function TabList({
  tabs,
  active,
  onChange,
  'aria-label': ariaLabel,
}: {
  tabs: TabDef[];
  active: string;
  onChange: (id: string) => void;
  'aria-label': string;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const idx = tabs.findIndex((t) => t.id === active);
    let nextIdx: number;
    if (e.key === 'ArrowRight') nextIdx = (idx + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = tabs.length - 1;
    else return;
    e.preventDefault();
    const nextTab = tabs[nextIdx];
    if (!nextTab) return;
    onChange(nextTab.id);
    listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[nextIdx]?.focus();
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className="flex gap-6 border-b border-border"
    >
      {tabs.map((tab) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-controls={`panel-${tab.id}`}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={(e) => {
              onChange(tab.id);
              // Explicit focus, not left to the browser's default click
              // behaviour — Safari doesn't focus <button> on click, so
              // without this a mouse click can leave focus wherever it
              // was (e.g. a field in the panel that's about to be
              // hidden), which then gets force-blurred to <body> with no
              // landing point. Chromium/Firefox already focus on click,
              // so this is a no-op there.
              e.currentTarget.focus();
            }}
            className={[
              'flex items-center gap-2 rounded-t-sm border-b-2 px-1 py-3 text-sm font-semibold transition-colors',
              'focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none',
              selected
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            ].join(' ')}
          >
            {tab.label}
            {tab.badge}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({
  id,
  active,
  children,
  className = 'space-y-6',
}: {
  id: string;
  active: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      role="tabpanel"
      id={`panel-${id}`}
      aria-labelledby={`tab-${id}`}
      hidden={!active}
      className={className}
    >
      {children}
    </div>
  );
}
